import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {useInputSearch} from '@/hooks/useInputSearch'
import {z} from 'zod'
import {PaginationResponse} from '@/types'
import {httpGetNeuromuscularDiagnosis, HttpGetNeuromuscularDiagnosisOptions} from './patients.http'
import {NeuroMusocolarDiagnosis} from '../types'

export const HttpGetNeuromuscularDiagnosisResponse = z.object({
    data: z.array(NeuroMusocolarDiagnosis),
    meta: PaginationResponse
})
export type HttpGetNeuromuscularDiagnosisResponse = z.infer<typeof HttpGetNeuromuscularDiagnosisResponse>

export const useNeuromuscularDiagnosis = (promiseArgs: Omit<HttpGetNeuromuscularDiagnosisOptions, 'page'>) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.NEUROMUSCULAR_DIAGNOSIS, searchValue, promiseArgs],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetNeuromuscularDiagnosis({
                    page: pageParam,
                    search: searchValue,
                    limit: promiseArgs.limit,
                    responseType: promiseArgs.responseType
                }),
                responseShape: HttpGetNeuromuscularDiagnosisResponse,
                onZodError: captureException
            }),
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
